import * as React from 'react';
import type { FeedRow } from '../../lib/feed.js';

interface FeedKeyboard {
  rows: readonly FeedRow[];
  focusedIndex: number;
  /** Whether the highlighted row is shown, so Enter, o and s may act on it. */
  armed: boolean;
  setFocusedIndex: (index: number) => void;
  toggle: (row: FeedRow) => void;
  open: (row: FeedRow) => void;
  markRead: (row: FeedRow) => void;
  focusSearch: () => void;
  collapse: () => void;
}

const EDITABLE = 'input, textarea, select, [contenteditable="true"], [role="dialog"]';

/** j/k move, Enter expands, o opens the record, s marks it read, / searches, Escape collapses. */
export function useFeedKeyboard(keys: FeedKeyboard) {
  const latest = React.useRef(keys);
  latest.current = keys;
  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as Element | null;
      if (target?.closest?.(EDITABLE)) return;
      const {
        rows,
        focusedIndex,
        armed,
        setFocusedIndex,
        toggle,
        open,
        markRead,
        focusSearch,
        collapse,
      } = latest.current;
      const row = armed ? rows[focusedIndex] : undefined;
      const move = (index: number) => {
        setFocusedIndex(Math.max(0, Math.min(rows.length - 1, index)));
        event.preventDefault();
      };
      switch (event.key) {
        case 'j':
          if (rows.length > 0) move(focusedIndex + 1);
          return;
        case 'k':
          if (rows.length > 0) move(focusedIndex - 1);
          return;
        case 'Enter':
          if (row && target?.closest?.('button, a') == null) {
            event.preventDefault();
            toggle(row);
          }
          return;
        case 'o':
          if (row) open(row);
          return;
        case 's':
          if (row) markRead(row);
          return;
        case '/':
          event.preventDefault();
          focusSearch();
          return;
        case 'Escape':
          collapse();
          return;
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);
}
